import Phaser from 'phaser';
import { Drop, DropType } from '../entities/Drop';
import { EntityManager } from '../entities/EntityManager';
import { MonsterType } from '../entities/Monster';
import { SessionManager } from './SessionManager';

// ============================================
// Drop System - Currency Pickups
// Based on economy_coins_gems_pickups.md
// ============================================

// Monster drop configuration
interface MonsterDropConfig {
  coins: {
    min: number;
    max: number;
    burstCountMin: number;
    burstCountMax: number;
  };
  gems: {
    chance: number;
    minValue: number;
    maxValue: number;
  };
}

const MONSTER_DROP_CONFIG: Record<MonsterType, MonsterDropConfig> = {
  // Tier 1 - Early Game
  crawler: {
    coins: { min: 2, max: 4, burstCountMin: 2, burstCountMax: 3 },
    gems: { chance: 0.02, minValue: 1, maxValue: 1 },
  },
  runner: {
    coins: { min: 1, max: 3, burstCountMin: 1, burstCountMax: 2 },
    gems: { chance: 0.02, minValue: 1, maxValue: 1 },
  },

  // Tier 2 - Mid Game
  brute: {
    coins: { min: 4, max: 8, burstCountMin: 3, burstCountMax: 6 },
    gems: { chance: 0.08, minValue: 1, maxValue: 2 },
  },
  shielded: {
    coins: { min: 3, max: 6, burstCountMin: 2, burstCountMax: 4 },
    gems: { chance: 0.06, minValue: 1, maxValue: 1 },
  },

  // Air Units
  bat: {
    coins: { min: 1, max: 2, burstCountMin: 1, burstCountMax: 2 },
    gems: { chance: 0.02, minValue: 1, maxValue: 1 },
  },
  flyer: {
    coins: { min: 2, max: 5, burstCountMin: 2, burstCountMax: 3 },
    gems: { chance: 0.05, minValue: 1, maxValue: 1 },
  },
  swarm: {
    coins: { min: 1, max: 1, burstCountMin: 1, burstCountMax: 1 },
    gems: { chance: 0.01, minValue: 1, maxValue: 1 },
  },

  // Late Game
  juggernaut: {
    coins: { min: 8, max: 15, burstCountMin: 5, burstCountMax: 8 },
    gems: { chance: 0.12, minValue: 1, maxValue: 3 },
  },
  phased: {
    coins: { min: 5, max: 10, burstCountMin: 3, burstCountMax: 5 },
    gems: { chance: 0.10, minValue: 1, maxValue: 2 },
  },
};

// Tree drop configuration
interface TreeDropConfig {
  playerProximityRadius: number;  // Player must be within this distance
  cooldownMin: number;  // Minimum seconds between drops from same tree
  cooldownMax: number;  // Maximum seconds between drops
  baseDropChance: number;  // Starting probability (10%)
  chanceIncreasePerWave: number;  // How much chance increases each wave
  maxDropChance: number;  // Cap the probability
  coinChance: number;  // When dropping, coin vs gem ratio
  gemChance: number;
  coinValueMin: number;
  coinValueMax: number;
  gemValue: number;
}

const TREE_DROP_CONFIG: TreeDropConfig = {
  playerProximityRadius: 50,  // Player must be touching/near the tree
  cooldownMin: 2,
  cooldownMax: 4,
  baseDropChance: 0.05,  // 5% starting chance (1/20)
  chanceIncreasePerWave: 0.05,  // +5% per wave without tower
  maxDropChance: 0.50,  // Cap at 50%
  coinChance: 0.92,
  gemChance: 0.08,
  coinValueMin: 10,     // Trees give 10 coins when they drop
  coinValueMax: 10,
  gemValue: 1,          // Or 1 gem
};

// Monster drop burst config — speeds in pixels per second (frame-rate independent)
const MONSTER_BURST_CONFIG = {
  coinSpeedMin: 240,   // ~4 px/frame at 60fps
  coinSpeedMax: 660,   // ~11 px/frame at 60fps
  gemSpeedMin: 180,
  gemSpeedMax: 420,
};

// Magnet pickup configuration (strength = px/s added toward player per second when in range)
const MAGNET_CONFIG = {
  startRadius: 130,   // When suction begins
  pickupRadius: 20,   // When collected
  strength: 2800,     // Speed (px/s) added toward player — strong visible pull
  gemRadiusBonus: 20, // Gems have larger magnet radius
};

// Player spill configuration: total coins → 80%, half of that spills as pickups (same burst as monsters)
const SPILL_CONFIG = {
  totalCoinsPercent: 0.80,  // Total coins reduced to 80% of current
  spillHalfOfTotal: true,  // Half of the new total spills out (player must pick up)
  minDrops: 4,
  maxDrops: 20,
  burstSpeedMin: 240,      // px/s — same as monster
  burstSpeedMax: 660,
  graceSeconds: 5.0,       // 5 second cooldown with blinking
  minCoinsToSpill: 1,      // Need at least 1 coin to trigger spill
  spillPickupDelayMs: 500, // Spiller can't pick up their own coins for 500ms
};

class DropSystemManager {
  private scene: Phaser.Scene | null = null;
  private drops: Set<Drop> = new Set();
  private dropIdCounter: number = 0;

  // Tree loot timers (cooldowns)
  private treeTimers: Map<string, number> = new Map();

  // Tree wave tracking - how many waves passed without a tower
  private treeWaveCount: Map<string, number> = new Map();
  private currentWave: number = 0;

  // Tree loot caps - track coins and gems dropped per tree
  private treeCoinsDropped: Map<string, number> = new Map();  // Max 10 coins per tree
  private treeGemsDropped: Map<string, number> = new Map();   // Max 1 gem per tree

  // Gem pity system
  private killsSinceLastGem: number = 0;
  private gemPityBonus: number = 0;

  // Player hit grace timers
  private playerGraceTimers: Map<string, number> = new Map();

  // Callbacks
  public onCoinsCollected?: (amount: number) => void;
  public onGemsCollected?: (amount: number) => void;

  init(scene: Phaser.Scene): void {
    this.scene = scene;
    this.drops.clear();
    this.treeTimers.clear();
    this.treeWaveCount.clear();
    this.treeCoinsDropped.clear();
    this.treeGemsDropped.clear();
    this.currentWave = 0;
    this.dropIdCounter = 0;
    this.killsSinceLastGem = 0;
    this.gemPityBonus = 0;
    this.playerGraceTimers.clear();

    console.log('[DropSystem] Initialized');
  }

  shutdown(): void {
    this.drops.forEach(drop => {
      if (!drop.destroyed) drop.destroy();
    });
    this.drops.clear();
    this.treeTimers.clear();
    this.scene = null;
  }

  /**
   * Spawn drops when a tower is sold — coins and gems explode out in the same burst as monster/tree drops.
   */
  spawnTowerSellDrops(x: number, y: number, coinTotal: number, gemCount: number): void {
    if (!this.scene) return;
    if (coinTotal <= 0 && gemCount <= 0) return;

    // Same burst style as monsters: 4–8 coin drops with random directions and speeds (240–660 px/s)
    const burstCount = Math.max(4, Math.min(8, Math.ceil(coinTotal / 12)));
    let remainingCoins = coinTotal;

    for (let i = 0; i < burstCount && remainingCoins > 0; i++) {
      const value = Math.min(Math.ceil(coinTotal / burstCount), remainingCoins);
      if (value <= 0) break;
      remainingCoins -= value;
      const velocity = this.getRandomBurstVelocity(
        MONSTER_BURST_CONFIG.coinSpeedMin,
        MONSTER_BURST_CONFIG.coinSpeedMax
      );
      this.spawnDrop('coin', value, x, y, velocity, 'tower_sell');
    }

    for (let i = 0; i < gemCount; i++) {
      const velocity = this.getRandomBurstVelocity(
        MONSTER_BURST_CONFIG.gemSpeedMin,
        MONSTER_BURST_CONFIG.gemSpeedMax
      );
      this.spawnDrop('gem', 1, x, y, velocity, 'tower_sell');
    }
  }

  /**
   * Spawn drops from a killed monster
   */
  spawnMonsterDrops(monsterType: MonsterType, x: number, y: number): void {
    if (!this.scene) return;

    const config = MONSTER_DROP_CONFIG[monsterType];
    if (!config) {
      console.warn(`[DropSystem] No drop config for monster type: ${monsterType}`);
      return;
    }

    // Calculate total coins
    const totalCoins = Phaser.Math.Between(config.coins.min, config.coins.max);
    const burstCount = Phaser.Math.Between(config.coins.burstCountMin, config.coins.burstCountMax);

    // Split coins across multiple drops
    const coinsPerDrop = Math.ceil(totalCoins / burstCount);
    let remainingCoins = totalCoins;

    for (let i = 0; i < burstCount && remainingCoins > 0; i++) {
      const value = Math.min(coinsPerDrop, remainingCoins);
      remainingCoins -= value;

      // Tight burst - coins stay close to death location
      const velocity = this.getRandomBurstVelocity(
        MONSTER_BURST_CONFIG.coinSpeedMin,
        MONSTER_BURST_CONFIG.coinSpeedMax
      );
      this.spawnDrop('coin', value, x, y, velocity, 'monster');
    }

    // Check for gem drop
    const gemChance = config.gems.chance + this.gemPityBonus;
    if (Math.random() < gemChance) {
      const gemValue = Phaser.Math.Between(config.gems.minValue, config.gems.maxValue);
      // Tight burst for gems too
      const velocity = this.getRandomBurstVelocity(
        MONSTER_BURST_CONFIG.gemSpeedMin,
        MONSTER_BURST_CONFIG.gemSpeedMax
      );
      this.spawnDrop('gem', gemValue, x, y, velocity, 'monster');

      // Reset pity
      this.killsSinceLastGem = 0;
      this.gemPityBonus = 0;
    } else {
      // Increase pity counter
      this.killsSinceLastGem++;
      if (this.killsSinceLastGem >= 20) {
        this.gemPityBonus += 0.02;
      }
    }
  }

  /**
   * Spawn drops from a killed boss
   */
  spawnBossDrops(x: number, y: number): void {
    if (!this.scene) return;

    // Bosses drop lots of coins and guaranteed gems
    const totalCoins = Phaser.Math.Between(80, 150);
    const burstCount = 12;
    const coinsPerDrop = Math.ceil(totalCoins / burstCount);
    let remainingCoins = totalCoins;

    for (let i = 0; i < burstCount && remainingCoins > 0; i++) {
      const value = Math.min(coinsPerDrop, remainingCoins);
      remainingCoins -= value;

      const velocity = this.getRandomBurstVelocity(6720, 12600); // px/s — boss coin burst
      this.spawnDrop('coin', value, x, y, velocity, 'monster');
    }

    // Guaranteed gem drops (multiple)
    const gemCount = Phaser.Math.Between(3, 5);
    for (let i = 0; i < gemCount; i++) {
      const gemValue = Phaser.Math.Between(8, 15);
      const velocity = this.getRandomBurstVelocity(5040, 10080); // px/s — boss gem burst
      this.spawnDrop('gem', gemValue, x, y, velocity, 'monster');
    }

    console.log(`[DropSystem] Boss dropped ${totalCoins} coins and ${gemCount} gems`);
  }

  /**
   * Initialize tree cooldown (ready to drop immediately)
   */
  registerTree(treeId: string): void {
    // Set to 0 so tree is ready to drop when player first walks over
    this.treeTimers.set(treeId, 0);
    // Start at wave 0 for probability calculation
    this.treeWaveCount.set(treeId, 0);
    // Initialize drop tracking (max 10 coins, max 1 gem per tree)
    this.treeCoinsDropped.set(treeId, 0);
    this.treeGemsDropped.set(treeId, 0);
  }

  /**
   * Called when a new wave starts - increases drop chance for unoccupied trees
   */
  onWaveStart(waveNumber: number): void {
    this.currentWave = waveNumber;
  }

  /**
   * Called when a wave ends - increment wave count for unoccupied trees
   */
  onWaveEnd(occupiedTreeIds: Set<string>): void {
    this.treeWaveCount.forEach((count, treeId) => {
      if (!occupiedTreeIds.has(treeId)) {
        // Tree is still unoccupied, increase its wave count (higher drop chance)
        this.treeWaveCount.set(treeId, count + 1);
      } else {
        // Tree has a tower, reset wave count
        this.treeWaveCount.set(treeId, 0);
      }
    });
  }

  /**
   * Update tree drops - only drop when player is near an unoccupied tree
   * Probability starts at 10% and increases each wave the tree doesn't have a tower
   */
  updateTreeDrops(treeData: Map<string, { x: number; y: number; occupied: boolean }>): void {
    if (!this.scene) return;

    const now = Date.now();
    const players = SessionManager.getAllPlayerEntities();
    if (players.length === 0) return;

    treeData.forEach((data, treeId) => {
      // Skip if tree is occupied (has a tower)
      if (data.occupied) {
        // Reset wave count when tower is built
        this.treeWaveCount.set(treeId, 0);
        return;
      }

      // Check if cooldown has passed
      const cooldownEnd = this.treeTimers.get(treeId) || 0;
      if (now < cooldownEnd) return;

      // Check if any player is near this tree
      let playerNearby = false;
      for (const player of players) {
        const dist = Phaser.Math.Distance.Between(player.x, player.y, data.x, data.y);
        if (dist <= TREE_DROP_CONFIG.playerProximityRadius) {
          playerNearby = true;
          break;
        }
      }

      if (playerNearby) {
        // Calculate drop chance based on waves without tower
        const waveCount = this.treeWaveCount.get(treeId) || 0;
        const dropChance = Math.min(
          TREE_DROP_CONFIG.maxDropChance,
          TREE_DROP_CONFIG.baseDropChance + (waveCount * TREE_DROP_CONFIG.chanceIncreasePerWave)
        );

        // Roll for drop
        if (Math.random() < dropChance) {
          this.spawnTreeDrop(treeId, data.x, data.y);
        }

        // Set cooldown regardless of whether we dropped
        const cooldown = Phaser.Math.Between(
          TREE_DROP_CONFIG.cooldownMin * 1000,
          TREE_DROP_CONFIG.cooldownMax * 1000
        );
        this.treeTimers.set(treeId, now + cooldown);
      }
    });
  }

  /**
   * Spawn drops from a tree - same burst spray as monsters (multiple drops, random directions)
   * Trees can drop either coins OR gems (not both). Max 10 coins total per tree, max 1 gem per tree.
   */
  private spawnTreeDrop(treeId: string, x: number, y: number): void {
    if (!this.scene) return;

    // Check what this tree can still drop
    const coinsDropped = this.treeCoinsDropped.get(treeId) || 0;
    const gemsDropped = this.treeGemsDropped.get(treeId) || 0;
    const canDropCoins = coinsDropped < 10;
    const canDropGems = gemsDropped < 1;

    // If tree is depleted, no drop
    if (!canDropCoins && !canDropGems) return;

    const roll = Math.random();
    const dropY = y - 10;

    // Decide what to drop based on what's available
    if (roll < TREE_DROP_CONFIG.gemChance && canDropGems) {
      // Gem drop (rare) - max 1 per tree, same burst as monster gems
      const velocity = this.getRandomBurstVelocity(
        MONSTER_BURST_CONFIG.gemSpeedMin,
        MONSTER_BURST_CONFIG.gemSpeedMax
      );
      this.spawnDrop('gem', TREE_DROP_CONFIG.gemValue, x, dropY, velocity, 'tree');
      this.treeGemsDropped.set(treeId, gemsDropped + 1);
    } else if (canDropCoins) {
      // Coin drop - spray like monster: multiple drops with random velocities
      const maxRemaining = 10 - coinsDropped;
      const totalCoins = Math.min(TREE_DROP_CONFIG.coinValueMin, maxRemaining);
      if (totalCoins <= 0) return;

      const burstCount = Phaser.Math.Between(2, 4);
      const coinsPerDrop = Math.ceil(totalCoins / burstCount);
      let remaining = totalCoins;

      for (let i = 0; i < burstCount && remaining > 0; i++) {
        const value = Math.min(coinsPerDrop, remaining);
        remaining -= value;
        const velocity = this.getRandomBurstVelocity(
          MONSTER_BURST_CONFIG.coinSpeedMin,
          MONSTER_BURST_CONFIG.coinSpeedMax
        );
        this.spawnDrop('coin', value, x, dropY, velocity, 'tree');
      }
      this.treeCoinsDropped.set(treeId, coinsDropped + totalCoins);
    }
  }

  /**
   * Handle player being hit by a monster:
   * - Total coins reduced to 80% (shared pool; affects everyone)
   * - Half of that new total spills as pickups around the player (same burst as monster drops)
   * - 10 second grace period with invincibility blinking
   */
  handlePlayerHit(playerId: string, playerX: number, playerY: number): boolean {
    if (!this.scene) return false;

    // Check grace timer
    const graceEnd = this.playerGraceTimers.get(playerId) || 0;
    if (Date.now() < graceEnd) {
      return false; // Still in grace period
    }

    const totalCoins = SessionManager.getCoins();
    if (totalCoins < SPILL_CONFIG.minCoinsToSpill) {
      return false; // Not enough coins to spill
    }

    // New total = 80% of current (shared coins; affects everyone)
    const newTotal = Math.floor(totalCoins * SPILL_CONFIG.totalCoinsPercent);
    // Half of the new total spills out as pickups
    const spillCount = SPILL_CONFIG.spillHalfOfTotal
      ? Math.floor(newTotal / 2)
      : 0;
    // Keep in bank: new total minus what we're spilling
    const keepInBank = newTotal - spillCount;
    const amountToRemove = totalCoins - keepInBank;
    const lostAmount = totalCoins - newTotal; // 20% reduction (gone forever)

    // Deduct from shared pool (removes amountToRemove; leaves keepInBank)
    SessionManager.spendCoins(amountToRemove);

    // Spawn spill: one drop per coin so each coin flies in a random direction
    if (spillCount > 0) {
      const maxSpillDrops = 50; // Cap entities
      const numDrops = Math.min(spillCount, maxSpillDrops);
      const coinsPerDrop = Math.max(1, Math.floor(spillCount / numDrops));
      let remaining = spillCount;

      for (let i = 0; i < numDrops && remaining > 0; i++) {
        const value = Math.min(coinsPerDrop, remaining);
        remaining -= value;
        const velocity = this.getRandomBurstVelocity(
          SPILL_CONFIG.burstSpeedMin,
          SPILL_CONFIG.burstSpeedMax
        );
        this.spawnDrop('coin', value, playerX, playerY, velocity, 'player_spill', playerId);
      }
    }

    // Set grace timer (5 seconds)
    this.playerGraceTimers.set(playerId, Date.now() + SPILL_CONFIG.graceSeconds * 1000);

    // Show visual feedback (20% lost, rest spilled)
    this.showSpillFeedback(playerX, playerY, lostAmount);

    console.log(`[DropSystem] Player ${playerId} hit: coins ${totalCoins} → ${keepInBank} (80% = ${newTotal}, spilled ${spillCount}, lost ${lostAmount} forever)`);
    return true;
  }

  /**
   * Check if player is in grace period
   */
  isPlayerInGracePeriod(playerId: string): boolean {
    const graceEnd = this.playerGraceTimers.get(playerId) || 0;
    return Date.now() < graceEnd;
  }

  /**
   * Get remaining grace time in seconds
   */
  getGraceRemainingSeconds(playerId: string): number {
    const graceEnd = this.playerGraceTimers.get(playerId) || 0;
    const remaining = graceEnd - Date.now();
    return Math.max(0, remaining / 1000);
  }

  private showSpillFeedback(x: number, y: number, lostAmount: number = 0): void {
    if (!this.scene) return;

    // Main warning text
    const text = this.scene.add.text(x, y - 30, 'COINS LOST!', {
      fontSize: '18px',
      color: '#ff4444',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3,
    });
    text.setOrigin(0.5);
    text.setDepth(150);

    this.scene.tweens.add({
      targets: text,
      y: y - 70,
      alpha: 0,
      duration: 1200,
      onComplete: () => text.destroy(),
    });

    // Show permanent loss amount
    if (lostAmount > 0) {
      const lossText = this.scene.add.text(x, y - 10, `-${lostAmount} GONE FOREVER`, {
        fontSize: '12px',
        color: '#ff8888',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 2,
      });
      lossText.setOrigin(0.5);
      lossText.setDepth(150);

      this.scene.tweens.add({
        targets: lossText,
        y: y - 50,
        alpha: 0,
        duration: 1500,
        onComplete: () => lossText.destroy(),
      });
    }
  }

  /**
   * Spawn a single drop
   */
  private spawnDrop(
    dropType: DropType,
    value: number,
    x: number,
    y: number,
    velocity: { x: number; y: number },
    source: 'monster' | 'tree' | 'player_spill' | 'tower_sell',
    ownerPlayerId?: string
  ): Drop | null {
    if (!this.scene) return null;

    const dropId = `drop-${this.dropIdCounter++}`;
    const drop = new Drop(this.scene, {
      id: dropId,
      x,
      y,
      dropType,
      value,
      velocity,
      lifetime: 15,
      source,
      ownerPlayerId,
    });

    drop.onCollected = (val, type) => {
      if (type === 'coin') {
        SessionManager.addCoins(val);
        this.onCoinsCollected?.(val);
      } else {
        SessionManager.addGems(val);
        this.onGemsCollected?.(val);
      }
    };

    drop.onExpired = () => {
      this.drops.delete(drop);
    };

    this.drops.add(drop);
    EntityManager.add(drop);

    return drop;
  }

  private getRandomBurstVelocity(minSpeed: number, maxSpeed: number): { x: number; y: number } {
    const angle = Math.random() * Math.PI * 2;
    const speed = Phaser.Math.Between(minSpeed, maxSpeed);
    return {
      x: Math.cos(angle) * speed,
      y: Math.sin(angle) * speed,
    };
  }

  /**
   * Update all drops - handle magnet and collection
   */
  update(delta: number): void {
    if (!this.scene) return;

    const players = SessionManager.getAllPlayerEntities();
    if (players.length === 0) return;

    // Update each drop
    const dropsToRemove: Drop[] = [];

    this.drops.forEach(drop => {
      if (drop.destroyed) {
        dropsToRemove.push(drop);
        return;
      }

      // Check magnet and collection for each player
      for (const player of players) {
        // Spiller can't pick up their own spilled coins for 500ms
        if (!drop.canBeCollectedBy(player.getPlayerId())) continue;

        const dx = player.x - drop.x;
        const dy = player.y - drop.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        // Gem bonus radius
        const magnetRadius = drop.dropType === 'gem'
          ? MAGNET_CONFIG.startRadius + MAGNET_CONFIG.gemRadiusBonus
          : MAGNET_CONFIG.startRadius;

        // Check collection
        if (dist <= MAGNET_CONFIG.pickupRadius) {
          drop.collect();
          dropsToRemove.push(drop);
          break;
        }

        // Apply magnet force (strength scaled by distance so stronger when closer, and by delta for frame-rate independence)
        if (dist <= magnetRadius && drop.isMagnetizable()) {
          const forceFactor = 1 - (dist / magnetRadius);
          const force = MAGNET_CONFIG.strength * forceFactor * (delta / 1000);
          drop.applyMagnetForce(player.x, player.y, force);
        }
      }

      // Update drop physics
      drop.update(delta);
    });

    // Cleanup
    dropsToRemove.forEach(drop => this.drops.delete(drop));
  }

  /**
   * Get all active drops for debug/display
   */
  getActiveDrops(): Drop[] {
    return Array.from(this.drops).filter(d => !d.destroyed);
  }

  /**
   * Get current gem pity info
   */
  getPityInfo(): { kills: number; bonus: number } {
    return {
      kills: this.killsSinceLastGem,
      bonus: this.gemPityBonus,
    };
  }
}

// Singleton instance
export const DropSystem = new DropSystemManager();
